import dayjs from "dayjs";
import {
  getDueDatePart,
  getDueTimePart,
  getDueDateForApi,
} from "./dueDateTime";

export const VOLUNTEER_OPPORTUNITY_STATUS_OPTIONS = ["Open", "Closed"];

export const volunteerDrillChipStyle = {
  padding: "2px 6px",
  background: "#e9ecef",
  borderRadius: 4,
};

export const volunteerDrillClearBtnStyle = {
  padding: "2px 8px",
  fontSize: 12,
  border: "1px solid #dee2e6",
  borderRadius: 4,
  background: "#fff",
  cursor: "pointer",
  color: "#0d6efd",
};

export function normalizeVolunteerOpportunityStatus(value) {
  const raw = value != null ? String(value).trim() : "";
  if (!raw) return "Open";
  const match = VOLUNTEER_OPPORTUNITY_STATUS_OPTIONS.find((v) => v.toLowerCase() === raw.toLowerCase());
  return match || "Open";
}

export function getOpportunitySignupCount(obj) {
  const n = Number(obj?.signup_count);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

export function getOpportunityVolunteersNeeded(obj) {
  const n = Number(obj?.volunteers_needed);
  return Number.isFinite(n) ? Math.max(1, Math.floor(n)) : 1;
}

/** Drill subtitle / chip: human-readable status from URL param and/or toolbar filter. */
export function formatVolunteerDrillStatusLabel(statusFromUrl, statusFilter) {
  if (statusFilter) return statusFilter;
  if (statusFromUrl === "closed") return "Closed";
  if (statusFromUrl === "open") return "Open";
  return statusFromUrl || "";
}

export function formatOpportunityDatetime(value) {
  if (value == null || value === "") return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("MM/DD/YYYY h:mm A") : "—";
}

function formatDateTimeRangeDisplay(eventDate, startTime, endTime) {
  const dateStr = (eventDate || "").trim();
  if (!dateStr) return "—";
  const dateLabel = dayjs(dateStr).isValid() ? dayjs(dateStr).format("MM/DD/YYYY") : dateStr;
  const startIso = getDueDateForApi(dateStr, startTime || "09:00");
  const endIso = getDueDateForApi(dateStr, endTime || "10:00");
  const startT = dayjs(startIso).isValid() ? dayjs(startIso).format("h:mm A") : "—";
  const endT = dayjs(endIso).isValid() ? dayjs(endIso).format("h:mm A") : "—";
  return `${dateLabel} · ${startT} – ${endT}`;
}

/** Duration in hours from opportunity start/end datetimes; null when missing or invalid. */
export function getOpportunityDurationHours(startDatetime, endDatetime) {
  if (startDatetime == null || startDatetime === "" || endDatetime == null || endDatetime === "") {
    return null;
  }
  const start = dayjs(startDatetime);
  const end = dayjs(endDatetime);
  if (!start.isValid() || !end.isValid()) return null;
  const ms = end.valueOf() - start.valueOf();
  if (ms <= 0) return null;
  return Math.round((ms / (1000 * 60 * 60)) * 10) / 10;
}

/** Human-readable volunteering hours value (e.g. "45 min", "2 hr", "1.5 hr"). */
export function formatVolunteeringHoursValue(hours, { emptyLabel = "—" } = {}) {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return emptyLabel;
  if (h < 1) {
    const mins = Math.round(h * 60);
    return mins > 0 ? `${mins} min` : emptyLabel;
  }
  const rounded = Math.round(h * 10) / 10;
  return `${rounded} hr`;
}

/** Human-readable opportunity duration (e.g. "45 min", "2 hr", "1.5 hr"). */
export function formatOpportunityDuration(startDatetime, endDatetime) {
  return formatVolunteeringHoursValue(getOpportunityDurationHours(startDatetime, endDatetime));
}

/** Table/list: opportunity start–end (same day = date + time range; spans days = full datetimes). */
export function formatOpportunityDateTimeRange(startDatetime, endDatetime) {
  const startDate = getDueDatePart(startDatetime);
  const endDate = getDueDatePart(endDatetime);
  if (!startDate && !endDate) return "—";
  if (startDate && endDate && startDate === endDate) {
    return formatDateTimeRangeDisplay(
      startDate,
      getDueTimePart(startDatetime) || "09:00",
      getDueTimePart(endDatetime) || "10:00"
    );
  }
  const startLabel = formatOpportunityDatetime(startDatetime);
  const endLabel = formatOpportunityDatetime(endDatetime);
  if (startLabel === "—") return endLabel;
  if (endLabel === "—") return startLabel;
  return `${startLabel} – ${endLabel}`;
}

export function getVolunteerParticipantDisplayName(row) {
  const name = [row?.first_name, row?.last_name]
    .filter(Boolean)
    .map((s) => String(s).trim())
    .join(" ")
    .trim();
  return name || "—";
}

/** Sort key for volunteer participant / signup name columns. */
export function getVolunteerParticipantNameForSort(row) {
  return [row?.last_name, row?.first_name].filter(Boolean).join(", ").trim().toLowerCase();
}
